import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./ShopPage.css";
import { collection, getDocs, query, where, orderBy, limit, startAfter } from "firebase/firestore";
import { db, DEFAULT_STORE_ID } from "../../../firebase/firebase";
import { searchStoreProducts, getUserStoreProducts } from "../../../firebase/product-functions";
import { getSaleProductIds } from "../../../firebase/sales-functions";
import { getLocalizedCategories } from "../../../firebase/category-functions";
import { getHomePageContent } from "../../../firebase/settings-functions";
import { useLanguage } from "../../../context/LanguageContext";
import '@fortawesome/fontawesome-free/css/all.min.css';
import SortDropdownShop from "../../../components/shop/SortDropdownShop/SortDropdownShop";
import ShopFilterPanel from "../../../components/shop/ShopFilterPanel/ShopFilterPanel";
import ProductGrid from "../../../components/common/ProductGrid/ProductGrid";
import StructuredData from "../../../components/common/StructuredData/StructuredData";
import { useStoreSettings } from "../../../hooks/useStoreSettings";
import useCurrency from "../../../hooks/useCurrency";
import { useDynamicPageTitle } from "../../../hooks/useDynamicPageTitle";

const ShopPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { formatDisplay } = useCurrency();

  // Set dynamic page title using store settings
  useDynamicPageTitle('shop');

  // Translations for shop page
  const translations = {
    en: {
      allItems: "All Items",
      sales: "Sales",
      searchResults: "Search Results",
      found: "Found",
      result: "result",
      results: "results",
      for: "for",
      in: "in",

      popularSearches: "Popular searches:",
      productsFound: "products found",
      productFound: "product found",
      trySearching: "Try searching for:",
      noProductsMatch: "No products match your search.",
      noProductsFilter: "No products match your filters.",
      clearSearch: "Clear Search",
      resetFilters: "Reset Filters",
      searching: "Searching...",
      loadingProducts: "Loading products...",
      filter: "FILTER"
    },
    ar: {
      allItems: "جميع المنتجات",
      sales: "التخفيضات",
      searchResults: "نتائج البحث",
      found: "تم العثور على",
      result: "نتيجة",
      results: "نتائج",
      for: "لـ",
      in: "في",
      popularSearches: "البحث الشائع:",
      productsFound: "منتجات تم العثور عليها",
      productFound: "منتج تم العثور عليه",
      trySearching: "جرب البحث عن:",
      noProductsMatch: "لا توجد منتجات تطابق بحثك.",
      noProductsFilter: "لا توجد منتجات تطابق فلاترك.",
      clearSearch: "مسح البحث",
      resetFilters: "إعادة تعيين الفلاتر",
      searching: "جاري البحث...",
      loadingProducts: "جاري تحميل المنتجات...",
      filter: "فلتر"
    }
  };

  const t = translations[language] || translations.en;

  // Get URL parameters
  const searchParams = new URLSearchParams(location.search);
  const categoryFromUrl = searchParams.get("category");
  const searchQuery = searchParams.get("search");
  const onSaleFromUrl = searchParams.get("onSale") === "true";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageTitle, setPageTitle] = useState("All Items");
  const [showFilter, setShowFilter] = useState(false);
  const [selectedColor, setSelectedColor] = useState("");
  const [popularTags, setPopularTags] = useState([]);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Filters and sorting state
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl || "");
  const [priceRange, setPriceRange] = useState(0);
  const [sortOption, setSortOption] = useState("Recommended");
  const [showOnSale, setShowOnSale] = useState(onSaleFromUrl);
  const [saleProductIds, setSaleProductIds] = useState([]);
  const [homePageContent, setHomePageContent] = useState(null);

  // Pagination state (only for non-search mode)
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Get store ID from your default constant
  const storeId = DEFAULT_STORE_ID;

  // Get store currency from settings
  const { currency: storeCurrency, storeSettings } = useStoreSettings();

  // Helper function to get localized category name for display
  const getLocalizedCategoryNameForDisplay = (categoryName) => {
    if (!categoryName) return '';

    // Find the category in our localized categories array
    const category = categories.find(cat => {
      // Match against original English name or multilingual English name
      const originalName = cat.originalName || cat.multilingual?.en?.name || cat.name;
      return originalName === categoryName;
    });

    // Return localized name if found, otherwise fallback to original name
    return category ? category.name : categoryName;
  };

  // Helper function to get shop description from home page content
  const getShopDescription = () => {
    // Debug logging
    // console.log('🔍 getShopDescription - homePageContent:', homePageContent);
    // console.log('🔍 getShopDescription - language:', language);
    // console.log('🔍 getShopDescription - heroSection:', homePageContent?.heroSection);

    // The getHomePageContent already returns localized data, so we can access subtitle directly
    if (homePageContent?.heroSection?.subtitle) {
      // console.log('✅ Found subtitle directly:', homePageContent.heroSection.subtitle);
      return homePageContent.heroSection.subtitle;
    }

    // Final fallback to hardcoded translation
    // console.log('⚠️ Using fallback description');
    return t.shopDescription;
  };

  // Set page title based on selected category, search query, or sales filter
  useEffect(() => {
    if (searchQuery) {
      setPageTitle(`${t.searchResults}: "${searchQuery}"`);
      setIsSearchMode(true);
    } else if (showOnSale) {
      setPageTitle(t.sales || "Sales");
      setIsSearchMode(false);
    } else if (selectedCategory) {
      // Find the localized category name for display
      const category = categories.find(cat => {
        // Match against original English name or multilingual English name
        const originalName = cat.originalName || cat.multilingual?.en?.name || cat.name;
        return originalName === selectedCategory;
      });

      // Use localized name if found, otherwise fallback to selectedCategory
      const displayName = category ? category.name : selectedCategory;
      setPageTitle(displayName);
      setIsSearchMode(false);
    } else {
      setPageTitle(t.allItems);
      setIsSearchMode(false);
    }
  }, [selectedCategory, searchQuery, showOnSale, t, categories]); // Added categories and showOnSale as dependencies

  // When URL parameters change, update the selected category and sales filter
  useEffect(() => {
    setSelectedCategory(categoryFromUrl || "");
    setShowOnSale(onSaleFromUrl);
  }, [categoryFromUrl, onSaleFromUrl]);

  // Fetch sale product IDs when component mounts
  useEffect(() => {
    const fetchSaleProductIds = async () => {
      try {
        const storeId = DEFAULT_STORE_ID;
        const saleIds = await getSaleProductIds(storeId);
        setSaleProductIds(saleIds);
      } catch (error) {
        console.error("Error fetching sale product IDs:", error);
        setSaleProductIds([]);
      }
    };

    fetchSaleProductIds();
  }, []);

  // Fetch home page content for hero section description
  useEffect(() => {
    const fetchHomePageContent = async () => {
      try {
        // console.log('🔍 Fetching home page content for storeId:', storeId, 'language:', language);
        const content = await getHomePageContent(storeId, language);
        // console.log('✅ Received home page content:', content);
        setHomePageContent(content);
      } catch (error) {
        console.error("❌ Error fetching home page content:", error);
        setHomePageContent(null);
      }
    };

    if (storeId) {
      fetchHomePageContent();
    }
  }, [storeId, language]);


  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesRef = collection(db, "stores", storeId, "categories");
        const categoriesQuery = query(
          categoriesRef,
          where("status", "==", "Active"),
          orderBy("name")
        );

        const querySnapshot = await getDocs(categoriesQuery);

        if (querySnapshot.empty) {
          setCategories([]);
        } else {
          const categoriesData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            productCount: 0 // We'll set this to a placeholder value for now
          }));

          // Get localized categories based on current language
          const localizedCategories = getLocalizedCategories(categoriesData, language);
          setCategories(localizedCategories);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
        setError("Failed to load categories");
      }
    };

    fetchCategories();
  }, [storeId, language]); // Added language as dependency

  // Enhanced search function that searches through tags
  const performTagSearch = async () => {
    try {
      setLoading(true);
      setError("");

      const results = await searchStoreProducts(storeId, searchQuery, {
        category: selectedCategory || null,
        status: "active",
        limit: 100
      });      // Apply additional filters
      let filteredResults = results;

      // Apply sales filter
      if (showOnSale) {
        filteredResults = filteredResults.filter(product =>
          saleProductIds.includes(product.id)
        );
      }

      // Apply price filter
      if (priceRange > 0) {
        filteredResults = filteredResults.filter(product =>
          parseFloat(product.price) <= priceRange
        );
      }

      // Apply color filter
      if (selectedColor) {
        filteredResults = filteredResults.filter(product =>
          product.color === selectedColor ||
          (product.variants && product.variants.some(v => v.color === selectedColor))
        );
      }

      // Apply sorting
      const sortedResults = applySorting(filteredResults, sortOption);

      setProducts(sortedResults);
      setHasMore(false); // Search results don't support pagination

    } catch (err) {
      console.error("Error performing search:", err);
      setError("Search failed. Please try again.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Apply client-side filters
  const applyClientSideFilters = (products) => {
    let filtered = [...products];

    // Apply sales filter
    if (showOnSale) {
      filtered = filtered.filter(product =>
        saleProductIds.includes(product.id)
      );
    }

    // Apply price filter only if priceRange > 0
    if (priceRange > 0) {
      filtered = filtered.filter(product =>
        parseFloat(product.price) <= priceRange
      );
    }

    // Apply color filter
    if (selectedColor) {
      filtered = filtered.filter(product =>
        product.color === selectedColor ||
        (product.variants && product.variants.some(v => v.color === selectedColor))
      );
    }

    return filtered;
  };

  // Apply client-side sorting for search results
  const applySorting = (products, sortOption) => {
    const sorted = [...products];

    switch (sortOption) {
      case "New Product":
        return sorted.sort((a, b) => {
          const aDate = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
          const bDate = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
          return bDate - aDate;
        });
      case "Price low to high":
        return sorted.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      case "Price high to low":
        return sorted.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
      default:
        return sorted;
    }
  };

  // Regular product fetching for browsing
  const fetchRegularProducts = async () => {
    setLoading(true);
    setError("");
    setLastDoc(null); // Reset pagination when filters change

    try {
      // Map sort option to field and direction
      let sortBy = 'createdAt';
      let sortDirection = 'desc';

      switch (sortOption) {
        case "New Product":
          sortBy = 'createdAt';
          sortDirection = 'desc';
          break;
        case "Price low to high":
          sortBy = 'price';
          sortDirection = 'asc';
          break;
        case "Price high to low":
          sortBy = 'price';
          sortDirection = 'desc';
          break;
        default:
          // Default is "Recommended" - show newest first
          sortBy = 'createdAt';
          sortDirection = 'asc';
      }

      // Use the centralized function
      const result = await getUserStoreProducts(storeId, {
        limit: 20,
        lastDoc: null,
        category: selectedCategory || null,
        status: "active",
        sortBy: sortBy,
        sortDirection: sortDirection
      });

      if (result.products.length === 0) {
        setProducts([]);
        setHasMore(false);
      } else {
        // Apply client-side filters
        const filteredProducts = applyClientSideFilters(result.products);

        setProducts(filteredProducts);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError("Failed to load products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Main fetch products effect - choose between search and regular fetch
  useEffect(() => {
    if (searchQuery && searchQuery.trim()) {
      performTagSearch();
    } else {
      fetchRegularProducts();
    }
  }, [storeId, selectedCategory, searchQuery, priceRange, sortOption, selectedColor, showOnSale, saleProductIds]);

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);

    // Update URL params to reflect the category filter
    const currentSearch = searchQuery ? `search=${encodeURIComponent(searchQuery)}&` : '';
    if (category) {
      navigate(`/shop?${currentSearch}category=${encodeURIComponent(category)}`);
    } else {
      if (searchQuery) {
        navigate(`/shop?search=${encodeURIComponent(searchQuery)}`);
      } else {
        navigate("/shop");
      }
    }
  };

  // Handle sort change
  const handleSortChange = (option) => {
    setSortOption(option);
  };

  // Handle price range change
  const handlePriceRangeChange = (e) => {
    setPriceRange(parseInt(e.target.value, 10));
  };

  // Load more products (only for non-search mode)
  const loadMoreProducts = async () => {
    if (!hasMore || loading || loadingMore || isSearchMode) return;

    setLoadingMore(true);

    try {
      // Map sort option to field and direction
      let sortBy = 'createdAt';
      let sortDirection = 'desc';

      switch (sortOption) {
        case "New Product":
          sortBy = 'createdAt';
          sortDirection = 'desc';
          break;
        case "Price low to high":
          sortBy = 'price';
          sortDirection = 'asc';
          break;
        case "Price high to low":
          sortBy = 'price';
          sortDirection = 'desc';
          break;
        default:
          sortBy = 'createdAt';
          sortDirection = 'desc';
      }

      // Use the centralized function for pagination
      const result = await getUserStoreProducts(storeId, {
        limit: 20,
        lastDoc: lastDoc,
        category: selectedCategory || null,
        status: "active",
        sortBy: sortBy,
        sortDirection: sortDirection
      });

      if (result.products.length === 0) {
        setHasMore(false);
      } else {
        // Apply client-side filters to new products
        const filteredNewProducts = applyClientSideFilters(result.products);

        setProducts(prevProducts => [...prevProducts, ...filteredNewProducts]);
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      }
    } catch (err) {
      console.error("Error loading more products:", err);
      setError("Failed to load more products. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  };

  // Reset filters function
  const resetFilters = () => {
    setSelectedCategory("");
    setPriceRange(0);
    setSelectedColor("");
    setSortOption("Recommended");
    navigate("/shop");
  };

  // Format currency using centralized system
  const formatCurrency = formatDisplay;

  // Handle tag click from popular tags
  const handleTagClick = (tag) => {
    navigate(`/shop?search=${encodeURIComponent(tag)}`);
  };

  return (
    <>
      <StructuredData pageType="shop" />
      {/* Shop Filter Panel */}
      <ShopFilterPanel
        show={showFilter}
        onClose={() => setShowFilter(false)}
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
        selectedColor={selectedColor}
        onColorChange={setSelectedColor}
        priceRange={priceRange}
        onPriceChange={handlePriceRangeChange}
      />

      <div className="shop-page-container fade-in-page">
        {/* Title */}
        <div className="shop-section-header">
          <h1 className="shop-main-title animated-title">{pageTitle}</h1>
          <p className="shop-description animated-desc">
            {searchQuery
              ? `${t.found} ${products.length} ${products.length !== 1 ? t.results : t.result} ${t.for} "${searchQuery}"${selectedCategory ? ` ${t.in} ${getLocalizedCategoryNameForDisplay(selectedCategory)}` : ''}`
              : getShopDescription()
            }
          </p>

          {/* Show popular tags when not searching */}
          {!searchQuery && popularTags.length > 0 && (
            <div className="popular-tags-section">
              <span className="popular-tags-label">{t.popularSearches}</span>
              <div className="popular-tags">
                {popularTags.map((tag, index) => (
                  <button
                    key={index}
                    className="popular-tag"
                    onClick={() => handleTagClick(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Filter and Sort Bar */}
        <div className="shop-page-animated">
          <div className="shop-toolbar">
            <div className="shop-toolbar-left">
              <SortDropdownShop
                selectedOption={sortOption}
                onSortChange={handleSortChange}
              />
            </div>

            <div className="shop-toolbar-right">
              <button className="filter-btn" onClick={() => setShowFilter(true)}>
                <i className="fa-solid fa-sliders"></i> {t.filter}
              </button>
            </div>
          </div>

          {/* Search Results Info */}
          {searchQuery && (
            <div className="search-results-info">
              <div className="search-meta">
                <span className="results-count">
                  {products.length} {products.length !== 1 ? t.productsFound : t.productFound}
                </span>
                {selectedCategory && (
                  <span className="category-filter">{t.in} {getLocalizedCategoryNameForDisplay(selectedCategory)}</span>
                )}
              </div>

              {products.length === 0 && !loading && (
                <div className="search-suggestions">
                  <p>{t.trySearching}</p>
                  <div className="suggestion-tags">
                    {popularTags.slice(0, 5).map((tag, index) => (
                      <button
                        key={index}
                        className="suggestion-tag"
                        onClick={() => handleTagClick(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Product Grid */}
          <ProductGrid
            products={products}
            loading={loading}
            error={error}
            formatCurrency={formatCurrency}
            gridColumns={4}
            cardVariant="shop"
            loadingText={isSearchMode ? t.searching : t.loadingProducts}
            emptyStateText={searchQuery ? t.noProductsMatch : t.noProductsFilter}
            emptyStateAction={{
              text: searchQuery ? t.clearSearch : t.resetFilters,
              onClick: resetFilters
            }}
            showLoadMore={!isSearchMode}
            hasMore={hasMore}
            onLoadMore={loadMoreProducts}
            loadingMore={loadingMore}
          />
        </div>
      </div>
    </>
  );
};

export default ShopPage;